import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign } from '../types';

export type CampaignRow = Omit<Campaign, 'id'> & { id: string };
import { CampaignStatusTag } from '../components/CampaignStatusTag/CampaignStatusTag';

const EMPTY_VALUE = '--';

function formatCompactNumber(value: number): string {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;

    return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }

  if (value >= 1_000) {
    const thousands = value / 1_000;

    return `${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)}K`;
  }

  return value.toLocaleString();
}

function formatCurrency(cents: number): string {
  const dollars = cents / 100;

  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function useCampaignColumns() {
  const { t } = useTranslation('automated-creator-campaign');

  return useMemo(() => {
    const columnHelper = createColumnHelper<CampaignRow>();

    return [
      columnHelper.accessor('track_name', {
        id: 'track_name',
        header: () => t('table.track'),
        cell: (info) => (
          <div>
            <div className="font-medium text-strong">{info.getValue()}</div>
            <div className="text-caption-md text-primary-default">
              {info.row.original.artist_name}
            </div>
          </div>
        ),
        enableSorting: true,
        size: 200,
      }),

      columnHelper.accessor('run_date_start', {
        id: 'run_date_start',
        header: () => t('table.launchDate'),
        cell: (info) => formatDate(info.getValue()),
        enableSorting: true,
        size: 140,
      }),

      columnHelper.accessor('status', {
        id: 'status',
        header: () => t('table.status'),
        cell: (info) => <CampaignStatusTag status={info.getValue()} />,
        enableSorting: true,
        size: 130,
      }),

      columnHelper.accessor(
        (row) => row.most_recent_campaign_stats?.live_posts ?? null,
        {
          id: 'live_posts',
          header: () => t('table.liveClips'),
          cell: (info) => {
            const value = info.getValue();

            return value != null ? value.toLocaleString() : EMPTY_VALUE;
          },
          enableSorting: true,
          size: 110,
        },
      ),

      columnHelper.accessor(
        (row) => row.most_recent_campaign_stats?.total_views ?? null,
        {
          id: 'total_views',
          header: () => t('table.views'),
          cell: (info) => {
            const value = info.getValue();

            return value != null ? formatCompactNumber(value) : EMPTY_VALUE;
          },
          enableSorting: true,
          size: 100,
        },
      ),

      columnHelper.accessor(
        (row) => row.most_recent_campaign_stats?.cpm ?? null,
        {
          id: 'cpm',
          header: () => t('table.cpm'),
          cell: (info) => {
            const value = info.getValue();

            return value != null
              ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : EMPTY_VALUE;
          },
          enableSorting: true,
          size: 100,
        },
      ),

      columnHelper.accessor(
        (row) =>
          row.most_recent_campaign_stats?.budget_used_in_cents_usd ?? null,
        {
          id: 'total_spend',
          header: () => t('table.totalSpend'),
          cell: (info) => {
            const spend = info.getValue();
            const budget = info.row.original.budget;

            if (spend == null) {
              return EMPTY_VALUE;
            }

            return (
              <div>
                <div className="font-medium">{formatCurrency(spend)}</div>
                {budget != null ? (
                  <div className="text-caption-md text-subtle">
                    {t('table.budget', { amount: formatCurrency(budget) })}
                  </div>
                ) : null}
              </div>
            );
          },
          enableSorting: true,
          size: 140,
        },
      ),
    ];
  }, [t]);
}
